import { Controller } from '@hotwired/stimulus'

const STORAGE_KEY = 'flamingos_steps_view_preference'
const TRANSITION_DURATION = 150

export class RecipeStepNavigatorController extends Controller {
  static targets = [
    'step',
    'dot',
    'prevButton',
    'nextButton',
    'stepIndicator',
    'stepsContainer',
    'ingredientsPanel',
    'ingredientsContent',
    'ingredientsToggle',
    'viewToggle',
    'listContainer',
    'cardControls'
  ]

  static values = {
    current: { type: Number, default: 0 },
    total: Number,
    swipeThreshold: { type: Number, default: 50 },
    viewMode: { type: String, default: 'list' }
  }

  connect () {
    this.restoreViewPreference()
    this.updateUI()
    this.addKeyboardListeners()
    this.addSwipeListeners()
  }

  disconnect () {
    this.removeKeyboardListeners()
    this.removeSwipeListeners()
  }

  nextStep (event) {
    event?.preventDefault()
    if (this.canGoForward) {
      this.currentValue++
      this.updateUI()
    }
  }

  previousStep (event) {
    event?.preventDefault()
    if (this.canGoBack) {
      this.currentValue--
      this.updateUI()
    }
  }

  goToStep (event) {
    event?.preventDefault()
    const stepIndex = parseInt(event.currentTarget.dataset.step, 10)
    if (this.isValidStep(stepIndex)) {
      this.currentValue = stepIndex
      this.updateUI()
    }
  }

  goToFirst () {
    this.currentValue = 0
    this.updateUI()
  }

  goToLast () {
    this.currentValue = this.totalValue - 1
    this.updateUI()
  }

  toggleViewMode (event) {
    event?.preventDefault()
    const newMode = event.currentTarget.dataset.viewMode
    if (newMode === this.viewModeValue) return

    this.crossfadeTo(() => {
      if (newMode === 'card') {
        this.syncCardToVisibleListStep()
      }
      this.viewModeValue = newMode
      this.saveViewPreference()
      this.applyViewMode()
    })
  }

  toggleIngredients (event) {
    event?.preventDefault()
    this.ingredientsContentTarget?.classList.toggle('is-collapsed')
    this.ingredientsToggleTarget?.classList.toggle('is-collapsed')
  }

  // Private

  get canGoForward () {
    return this.currentValue < this.totalValue - 1
  }

  get canGoBack () {
    return this.currentValue > 0
  }

  get isListMode () {
    return this.viewModeValue === 'list'
  }

  isValidStep (index) {
    return !isNaN(index) && index >= 0 && index < this.totalValue
  }

  restoreViewPreference () {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved === 'list' || saved === 'card') {
        this.viewModeValue = saved
      }
    } catch {
      // localStorage unavailable
    }
    this.applyViewMode()
  }

  saveViewPreference () {
    try {
      localStorage.setItem(STORAGE_KEY, this.viewModeValue)
    } catch {
      // localStorage unavailable
    }
  }

  applyViewMode () {
    const isList = this.isListMode

    if (this.hasStepsContainerTarget) {
      this.stepsContainerTarget.classList.toggle('hidden', isList)
    }

    if (this.hasListContainerTarget) {
      this.listContainerTarget.classList.toggle('hidden', !isList)
    }

    this.cardControlsTargets.forEach((el) => {
      el.classList.toggle('hidden', isList)
    })

    this.updateToggleButtons()
  }

  updateToggleButtons () {
    if (!this.hasViewToggleTarget) return

    const buttons = this.viewToggleTarget.querySelectorAll('.tab')
    buttons.forEach((btn) => {
      btn.classList.toggle('tab-active', btn.dataset.viewMode === this.viewModeValue)
    })
  }

  syncCardToVisibleListStep () {
    if (!this.hasListContainerTarget) return

    const listItems = this.listContainerTarget.querySelectorAll('[data-step-index]')
    const containerRect = this.listContainerTarget.getBoundingClientRect()
    let closestIndex = 0
    let closestDistance = Infinity

    listItems.forEach((item) => {
      const rect = item.getBoundingClientRect()
      const distance = Math.abs(rect.top - containerRect.top)
      if (distance < closestDistance) {
        closestDistance = distance
        closestIndex = parseInt(item.dataset.stepIndex, 10)
      }
    })

    if (this.isValidStep(closestIndex)) {
      this.currentValue = closestIndex
      this.updateUI()
    }
  }

  crossfadeTo (callback) {
    const containers = [this.stepsContainerTarget, this.listContainerTarget].filter(Boolean)
    containers.forEach((el) => el.classList.add('transitioning'))

    setTimeout(() => {
      callback()
      containers.forEach((el) => el.classList.remove('transitioning'))
    }, TRANSITION_DURATION)
  }

  updateUI () {
    this.updateSteps()
    this.updateDots()
    this.updateButtons()
    this.updateIndicator()
  }

  updateSteps () {
    this.stepTargets.forEach((step, index) => {
      step.classList.toggle('is-active', index === this.currentValue)
    })
  }

  updateDots () {
    this.dotTargets.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === this.currentValue)
      dot.classList.toggle('is-completed', index < this.currentValue)
    })
  }

  updateButtons () {
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.disabled = !this.canGoBack
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.disabled = !this.canGoForward
    }
  }

  updateIndicator () {
    if (this.hasStepIndicatorTarget) {
      this.stepIndicatorTarget.textContent = `Step ${this.currentValue + 1}`
    }
  }

  addKeyboardListeners () {
    this.keydownHandler = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.keydownHandler)
  }

  removeKeyboardListeners () {
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler)
    }
  }

  handleKeydown (event) {
    if (this.isListMode) return
    if (this.isTypingInInput(event)) return

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault()
        this.nextStep()
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault()
        this.previousStep()
        break
      case 'Home':
        event.preventDefault()
        this.goToFirst()
        break
      case 'End':
        event.preventDefault()
        this.goToLast()
        break
    }
  }

  isTypingInInput (event) {
    return event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA'
  }

  addSwipeListeners () {
    if (!this.hasStepsContainerTarget) return

    this.touchStartX = 0
    this.touchEndX = 0

    this.touchStartHandler = this.handleTouchStart.bind(this)
    this.touchMoveHandler = this.handleTouchMove.bind(this)
    this.touchEndHandler = this.handleTouchEnd.bind(this)

    const options = { passive: true }
    this.stepsContainerTarget.addEventListener('touchstart', this.touchStartHandler, options)
    this.stepsContainerTarget.addEventListener('touchmove', this.touchMoveHandler, options)
    this.stepsContainerTarget.addEventListener('touchend', this.touchEndHandler, options)
  }

  removeSwipeListeners () {
    if (!this.hasStepsContainerTarget) return

    this.stepsContainerTarget.removeEventListener('touchstart', this.touchStartHandler)
    this.stepsContainerTarget.removeEventListener('touchmove', this.touchMoveHandler)
    this.stepsContainerTarget.removeEventListener('touchend', this.touchEndHandler)
  }

  handleTouchStart (event) {
    this.touchStartX = event.changedTouches[0].screenX
  }

  handleTouchMove (event) {
    this.touchEndX = event.changedTouches[0].screenX
  }

  handleTouchEnd () {
    if (this.isListMode) return

    const swipeDistance = this.touchStartX - this.touchEndX

    if (Math.abs(swipeDistance) > this.swipeThresholdValue) {
      swipeDistance > 0 ? this.nextStep() : this.previousStep()
    }
  }
}
